/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef } from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import { Pressable, type PressableProps } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	baseButton: {
		transition: 'background 0.1s ease-out, box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38)',
		fontWeight: token('font.weight.medium'),
		paddingBlock: token('space.075'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text'),
	},
	selectedButton: {
		color: token('color.text.selected'),
		textDecoration: 'underline',
		textDecorationThickness: token('border.width.selected'),
		textUnderlineOffset: token('space.100'),
	},
});

type TabButtonProps = Omit<PressableProps, 'xcss'> & {
	isSelected: boolean;
};

export const TabButton: ReturnType<typeof forwardRef<HTMLButtonElement, TabButtonProps>> =
	forwardRef<HTMLButtonElement, TabButtonProps>(
		(
			{ onClick, isSelected, children, id, 'aria-controls': ariaControls, tabIndex, onKeyDown },
			ref,
		): JSX.Element => {
			return (
				<Pressable
					ref={ref}
					role="tab"
					onClick={onClick}
					onKeyDown={onKeyDown}
					tabIndex={tabIndex}
					xcss={cx(styles.baseButton, isSelected && styles.selectedButton)}
					aria-selected={isSelected}
					id={id}
					aria-controls={ariaControls}
				>
					{children}
				</Pressable>
			);
		},
	);

TabButton.displayName = 'TabButton';
